import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { AdminShell, type AdminSectionId } from "@/components/admin/AdminShell";
import {
  BannersSectionAdmin,
  OverviewSection,
  PlansSectionAdmin,
} from "@/components/admin/CatalogSections";
import {
  BrandSection,
  CoverageSection,
  FooterSection,
  HighlightsSection,
  HostingSection,
  PartnersSection,
  PhoneSection,
  ServicesSection,
  InternetSection,
  CamerasSection,
  AppsSectionAdmin,
  BlogSection,
  JobsSection,
  StoresSection,
  AboutSection,
  PromoSection,
  IntegrationsSection,
} from "@/components/admin/ContentSections";
import { ApplicationsSection } from "@/components/admin/ApplicationsSection";

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({
    meta: [
      { title: "Painel administrativo — Contato Internet" },
      {
        name: "description",
        content: "Gestão de planos, banners, textos e imagens do site Contato.",
      },
      { property: "og:title", content: "Painel administrativo — Contato Internet" },
      { property: "og:description", content: "Gestão completa do conteúdo do site Contato." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminPage,
});

function AdminPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<"checking" | "admin" | "denied" | "error">("checking");
  const [section, setSection] = useState<AdminSectionId>("overview");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: userData } = await supabase.auth.getUser();
      const uid = userData.user?.id;
      if (!uid) {
        if (!cancelled) setStatus("denied");
        return;
      }

      const { data: roleRow } = await supabase
        .from("user_roles")
        .select("id")
        .eq("user_id", uid)
        .eq("role", "admin")
        .maybeSingle();
      if (roleRow) {
        if (!cancelled) setStatus("admin");
        return;
      }

      // Bootstrap do primeiro admin direto no client (SPA estática).
      const { count, error: countError } = await supabase
        .from("user_roles")
        .select("id", { count: "exact", head: true })
        .eq("role", "admin");
      if (countError) {
        if (!cancelled) setStatus("error");
        return;
      }
      if ((count ?? 0) === 0) {
        const { error } = await supabase.from("user_roles").insert({ user_id: uid, role: "admin" });
        if (!error) {
          if (!cancelled) setStatus("admin");
          return;
        }
      }
      if (!cancelled) setStatus("denied");
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function signOut() {
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  }

  if (status === "checking") {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  if (status === "denied" || status === "error") {
    return (
      <div className="mx-auto max-w-md px-5 py-24 text-center">
        <h1 className="text-2xl font-extrabold">
          {status === "error" ? "Não foi possível verificar o acesso" : "Acesso restrito"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {status === "error"
            ? "Tente recarregar a página. Se o problema continuar, entre novamente."
            : "Sua conta não tem permissão de administrador."}
        </p>
        <Button className="mt-6 rounded-full" onClick={signOut}>
          Sair
        </Button>
      </div>
    );
  }

  return (
    <AdminShell active={section} onSelect={setSection}>
      {section === "overview" && <OverviewSection />}
      {section === "plans" && <PlansSectionAdmin />}
      {section === "banners" && <BannersSectionAdmin />}
      {section === "highlights" && <HighlightsSection />}
      {section === "services" && <ServicesSection />}
      {section === "coverage" && <CoverageSection />}
      {section === "partners" && <PartnersSection />}
      {section === "phone" && <PhoneSection />}
      {section === "hosting" && <HostingSection />}
      {section === "internet" && <InternetSection />}
      {section === "cameras" && <CamerasSection />}
      {section === "apps" && <AppsSectionAdmin />}
      {section === "blog" && <BlogSection />}
      {section === "jobs" && <JobsSection />}
      {section === "applications" && <ApplicationsSection />}
      {section === "stores" && <StoresSection />}
      {section === "about" && <AboutSection />}
      {section === "promo" && <PromoSection />}
      {section === "integrations" && <IntegrationsSection />}
      {section === "brand" && <BrandSection />}
      {section === "footer" && <FooterSection />}
    </AdminShell>
  );
}
